import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { DateTime } from "luxon";
import { IoIosArrowForward, IoIosRefresh } from "react-icons/io";
import {
  residenceClientAssginment,
  SOARResources,
  residences,
} from "../services/soar";
import CensusCell from "./CensusCell";
import FloatingInput from "../common/FloatingInput";

function ClientDailyServices(props) {
  const [data, setData] = useState([]);
  const [residenceInfo, setResidenceInfo] = useState({});
  const { clientid, residenceid, bedid } = useParams();
  const [searchData, setSearchData] = useState({
    beginDate: "2024-10-30", // Format as YYYY-MM-DD
    endDate: new Date().toISOString().split("T")[0], // Today formatted
    hideConfirmed: false,
  });
  const applicationId =
    process.env.REACT_APP_ENVIRONMENT === "Production"
      ? "5dae139e-d052-4bd8-bf4a-499a31ac3c15"
      : "58990f83-8566-4ba8-b4b5-584a64e4cba1";
  const navigate = useNavigate();

  useEffect(() => {
    getBedAssignmentServices();
    getResidenceInfo();
  }, []);

  const getBedAssignmentServices = async () => {
    const data = await residenceClientAssginment({
      command: "query",
      application: applicationId,
      keyword: "datesresidenceinterms",
      searchTerms: {
        startDate: searchData.beginDate, // Format as YYYY-MM-DD
        endDate: searchData.endDate, // Today formatted as YYYY-MM-DD
        residence: residenceid,
      },
    });
    if (data.code !== "success") {
      toast.error("Error fetching services:", data.message);
      return;
    }

    getClientInfo(
      [clientid],
      data.data.filter((bedAssignment) => bedAssignment.client === clientid)
    );
  };

  const getClientInfo = async (clientIds, allServices) => {
    const data = await SOARResources({
      command: "query",
      application: applicationId,
      keyword: "modalityandcaregiver",
      resource: "censusinfo",
      ids: clientIds,
    });
    if (data.code !== "success") {
      toast.error("Error fetching client info:", data.message);
      return null;
    }
    // console.log("Client Info Data:", data.data);
    const clientData = data?.data?.map((client) => ({
      key: client.id,
      id: client.id,
      mpi: client.mpi,
      dob: client.dob,
      payors:
        allServices.find((service) => service.client === client.id)?.payors ||
        [],
      modalities: client.modalities || [],
      caregiver: client.caregiver || [],
    }));

    const allData = allServices.map((service) => ({
      key: service.id,
      ...service,
      modalities:
        clientData.find((info) => info.id === service.client)?.modalities || [],
      caregiver:
        clientData.find((info) => info.id === service.client)?.caregiver || [],
      dob: clientData.find((info) => info.id === service.client)?.dob,
      mpi: clientData.find((info) => info.id === service.client)?.mpi,
    }));
    // console.log("All Data with Client Info:", allData);
    setData(allData);
  };

  const getResidenceInfo = async () => {
    const resp = await residences({
      application: applicationId,
      command: "query",
      keyword: "byid",
      ids: [residenceid],
    });

    if (resp.code !== "success") {
      toast.error("Error fetching residence info:", resp.message);
      return;
    }
    setResidenceInfo(resp.data[0]);
  };

  const openType = () => {
    if (residenceInfo.type !== "FosterHome") {
      console.log("matching program type", residenceInfo.type);
      navigate(`/residence/programs/${residenceInfo.type}`);
    } else {
      navigate(`/residence/fosterhomes`);
    }
  };

  const openResidence = () => {
    if (residenceInfo.type !== "FosterHome") {
      navigate(`/residence/programs/${residenceInfo.type}/${residenceid}`);
    } else {
      navigate(`/residence/fosterhomes/${residenceid}`);
    }
  };

  return (
    <div>
      <div className="btn btn-light border-dark" onClick={() => openType()}>
        <div className="h5">
          {residenceInfo.type === "FosterHome"
            ? "Foster Homes"
            : residenceInfo.type === "family preservation"
            ? "Family Preservation"
            : "Program"}
        </div>
      </div>
      <IoIosArrowForward size="30" />
      <div className="btn btn-info border-dark" onClick={() => openResidence()}>
        <div className="h5">
          <i className="fas fa-home text-white me-2" />
          {data[0] && `${data[0].residenceName} Home`}
        </div>
      </div>
      <IoIosArrowForward size="30" />
      <div
        className="btn btn-primary border-dark"
        onClick={() => navigate(`/client/${data[0].client}`)}
      >
        <div className="h5">
          <i className="fas fa-user text-white me-2" />
          {data[0] && `${data[0].clientName} `}
        </div>
      </div>
      <div className="row my-4">
        <div className="col-2">
          <FloatingInput
            label="Start Date"
            type="date"
            value={searchData.beginDate}
            onChange={(e) =>
              setSearchData({ ...searchData, beginDate: e.target.value })
            }
          />
        </div>
        <div className="col-2">
          <FloatingInput
            label="End Date"
            type="date"
            value={searchData.endDate}
            onChange={(e) =>
              setSearchData({ ...searchData, endDate: e.target.value })
            }
          />
        </div>
        <div className="col-2">
          <IoIosRefresh
            className="text-primary mt-2"
            size="40"
            onClick={() => getBedAssignmentServices()}
          />
        </div>
        <div className="col">
          <div className="d-inline-block ms-4 form-check form-switch form-check-reverse float-end">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              id="expired"
              checked={searchData.hideConfirmed}
              onChange={(e) => {
                setSearchData({
                  ...searchData,
                  hideConfirmed: e.target.checked,
                });
              }}
            />
            <label className="form-check-label" htmlFor="expired">
              Hide Confirmed
            </label>
          </div>
        </div>
      </div>
      <div>
        {data.map((bedAssignment) => (
          <div key={bedAssignment.id}>
            {bedAssignment.services && (
              <>
                <div className="h4 text-primary ms-4">
                  {`${bedAssignment.bedName} (${DateTime.fromISO(
                    bedAssignment.admission
                  ).toLocaleString()} - ${
                    bedAssignment.discharge
                      ? DateTime.fromISO(
                          bedAssignment.discharge
                        ).toLocaleString()
                      : "Current"
                  })`}
                </div>
                <div className="d-flex flex-wrap">
                  {bedAssignment.services?.map((service) => (
                    <CensusCell
                      key={service.id}
                      bedAssignment={bedAssignment}
                      service={{
                        ...service,
                        serviceStatuses:
                          bedAssignment.serviceConfigurationValue
                            .serviceStatuses,
                      }}
                      setData={setData}
                      showConfirmed={!searchData.hideConfirmed}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default ClientDailyServices;
